import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Skeleton,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useCallback, useEffect, useState } from "react";
import { getPaymentHistory } from "../../../api/api";

const formatPrice = (paise) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: Number(paise) % 100 === 0 ? 0 : 2,
  }).format((Number(paise) || 0) / 100);

const formatDate = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));

const statusColor = (status) => {
  if (status === "COMPLETED") return "success";
  if (status === "PENDING") return "warning";
  if (status === "FAILED") return "error";
  return "default";
};

const PaymentHistory = () => {
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [error, setError] = useState("");

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getPaymentHistory();
      setPaymentHistory(Array.isArray(response?.data) ? response.data : []);
    } catch (requestError) {
      setError(
        requestError?.response?.data?.error?.message ||
          requestError?.response?.data?.message ||
          "Unable to load payment history.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  return (
    <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", pb: 5 }}>
      <Box
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderRadius: 3,
          bgcolor: "rgba(255,128,0,.08)",
          border: "1px solid rgba(255,128,0,.14)",
        }}
      >
        <Box sx={{ width: 50, height: 50, display: "grid", placeItems: "center", borderRadius: 2, bgcolor: "primary.main", color: "white" }}>
          <ReceiptLongRoundedIcon />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800}>Payment history</Typography>
          <Typography color="text.secondary" fontSize={13}>Review your membership payments and their status.</Typography>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" startIcon={<RefreshRoundedIcon />} onClick={fetchPayments}>Retry</Button>}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((item) => (
            <Grid key={item} size={{ xs: 12, md: 6 }}>
              <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : paymentHistory.length ? (
        <Grid container spacing={2}>
          {paymentHistory.map((payment) => (
            <Grid key={payment.id} size={{ xs: 12, md: 6 }}>
              <Card elevation={0} sx={{ height: "100%", p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                  <Box sx={{ display: "flex", gap: 1.25 }}>
                    <Box sx={{ width: 40, height: 40, display: "grid", placeItems: "center", borderRadius: 2, bgcolor: "rgba(255,128,0,.1)", color: "primary.main" }}>
                      <AccountBalanceWalletRoundedIcon fontSize="small" />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800}>{formatPrice(payment.amount_paise)}</Typography>
                      <Typography fontSize={12} color="text.secondary">{formatDate(payment.created_at)}</Typography>
                    </Box>
                  </Box>
                  <Chip label={payment.status} color={statusColor(payment.status)} size="small" sx={{ fontWeight: 750 }} />
                </Box>

                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <Box>
                    <Typography fontSize={11} color="text.secondary">Provider</Typography>
                    <Typography fontSize={13} fontWeight={650} textTransform="capitalize">{payment.provider || "—"}</Typography>
                  </Box>
                  <Box>
                    <Typography fontSize={11} color="text.secondary">Payment mode</Typography>
                    <Typography fontSize={13} fontWeight={650}>{payment.payment_mode?.replaceAll("_", " ") || "—"}</Typography>
                  </Box>
                  <Box sx={{ gridColumn: "1 / -1" }}>
                    <Typography fontSize={11} color="text.secondary">Order reference</Typography>
                    <Typography fontSize={12} fontWeight={650} sx={{ overflowWrap: "anywhere" }}>{payment.merchant_order_id}</Typography>
                  </Box>
                </Box>

                {payment.failure_reason && (
                  <Alert severity="error" sx={{ mt: 2 }}>{payment.failure_reason}</Alert>
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : !error ? (
        <Box sx={{ py: 8, textAlign: "center", border: "1px dashed", borderColor: "divider", borderRadius: 3 }}>
          <ReceiptLongRoundedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>No payments yet</Typography>
          <Typography color="text.secondary" fontSize={13}>Completed and pending payments will appear here.</Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default PaymentHistory;
